import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { LeapV2Session, LeapV2Report, LeapV2SessionReport, LeapV2SessionAttendanceSubmit, LeapV2SessionReportStudentSubmit, LeapV2SessionReportSubmit, LeapV2SessionReportResponse, LeapV2SessionSaveResponse } from '../models/leap-v2-session.model';
import { LeapV2Student, LeapV2StudentProgress } from '../models/leap-v2-student.model';
import { buildEvidenceDtos } from './leap-v2-progress-report.service';
import { StudentAttendance } from '../models/leap-v2-attendance.model';
import { BaseService } from '../../../../_services/base/base.service';
import { AppConfig } from '../../../../app.config';
import { HttpClient } from '@angular/common/http';
import { SessionUserHashManagerService } from '../../../../_services/session-user-hash-manager/session-user-hash-manager.service';
import { environment } from '../../../../../environments/environment';

/**
 * Data access for the LEAP v2 session flow. All methods call the TutorPortalAPI
 * Session endpoints (api/Session/SessionReport/...). saveSession posts the report as a draft;
 * submitProgress posts it as a finalised submit — both hit the Report endpoint, differing only
 * in the `submitted` flag the server uses to drive the status lifecycle.
 */
@Injectable({providedIn: 'root'})
export class LeapV2SessionService extends BaseService {

  private apiUrl: string = environment.apiUrl;
  // Picked evidence files the API has already stored; see toReportStudent.
  private readonly uploadedFiles = new WeakSet<File>();

  constructor(
    private http: HttpClient,
    private config: AppConfig,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith("/") ? "" : "/";
  }

  /**
   * Loads the report for ONE Orbit session. Previously keyed on a date, which could not distinguish two
   * sessions on the same placement and day (SessionIdentityGrain.md §4). loadingPage: the page is loading the
   * booking in its URL, so not found reads as one plain message (40973).
   */
  public loadSession(placementId: number, orbitSessionId: number, loadingPage: boolean = false): Observable<LeapV2SessionReport> {
    // GET sends no body, so the request hash must cover the empty-body placeholder (prepareOptions(null)).
    return this.http.get(this.apiUrl + 'api/Session/SessionReport/' + placementId + '/' + orbitSessionId,
      loadingPage ? this.prepareBookingLoadOptions(null) : this.prepareOptions(null)).pipe(
      map((data: any) => {
        return this.mapResponse(data.body);
      }), catchError(loadingPage ? this.handleBookingLoadError('getV2SessionReport') : this.handleError('getV2SessionReport'))
    );
  }

  /**
   * Adapts the API's wrapper response into the nested LeapV2SessionReport the component consumes,
   * mirroring the response shape ({sessionId, placementId, session, report, tutorCancellation,
   * behaviouralConcerns}). `session`/`report` are null when no session exists yet, so everything is
   * null-guarded to yield a usable empty form. Numeric ids are coerced to string to match the
   * component's string-keyed comparisons. Behavioural concerns stay on the report as the server-owned list
   * (one per student per session); the component looks them up by student id.
   */
  private mapResponse(res: LeapV2SessionReportResponse): LeapV2SessionReport {
    const session = res?.session ?? null;
    const report = res?.report ?? null;

    const students: LeapV2Student[] = (report?.students ?? []).map(s => ({
      id: String(s.id),
      name: s.name,
      progress: {
        attainmentLevels: s.progress?.attainmentLevels ?? [],
        objectiveComments: s.progress?.objectiveComments ?? '',
        engagement: s.progress?.engagement ?? 0,
        engagementComments: s.progress?.engagementComments ?? '',
        evidence: (s.progress?.evidence ?? []).map(e => ({
          name: e.name,
          size: e.size,
          contentType: e.contentType,
          url: e.url,
        })),
      } as LeapV2StudentProgress,
    }));

    const attendance: StudentAttendance[] = (session?.attendance ?? []).map(a => ({
      studentId: String(a.studentId),
      status: a.status,
      reason: a.reason,
      comments: a.comments,
      parentCarerInformed: a.parentCarerInformed,
    }));

    const sessionNode: LeapV2Session = {
      placementId: res?.placementId,
      date: session?.date ? new Date(session.date) : null,
      startTime: session?.startTime ?? '',
      endTime: session?.endTime ?? '',
      subject: session?.subject ?? '',
      sessionType: session?.sessionType ?? '',
      deliveryMode: session?.deliveryMode ?? '',
      status: session?.status ?? '',
      attendance,
      attendanceSubmitted: session?.attendanceSubmitted ?? false,
      consultantNotes: session?.consultantNotes ?? '',
      notesLastUpdated: session?.notesLastUpdated ? new Date(session.notesLastUpdated) : null,
      deliverySetting: session?.deliverySetting ?? null,
      previousDeliverySetting: session?.previousDeliverySetting ?? null,
    };

    const reportNode: LeapV2Report = {
      placementId: res?.placementId,
      date: report?.date ? new Date(report.date) : sessionNode.date,
      students,
      objectives: report?.objectives ?? [],
      // This session's own setting, else the previous session's as the default the tutor can change (39883).
      deliverySetting: session?.deliverySetting ?? session?.previousDeliverySetting ?? null,
    };

    return {
      sessionId: res?.sessionId,
      placementId: res?.placementId,
      // Identity is not carried in the GET body; the component stamps the route value onto this
      // immediately after load. Placeholder here only to satisfy the type.
      orbitSessionId: 0,
      session: sessionNode,
      report: reportNode,
      tutorCancellation: res?.tutorCancellation
        ? { reason: res.tutorCancellation.reason, comments: res.tutorCancellation.comments }
        : null,
      behaviouralConcerns: res?.behaviouralConcerns ?? [],
      rejection: res?.rejection
        ? { reason: res.rejection.reason, rejectedBy: res.rejection.rejectedBy, rejectedDate: new Date(res.rejection.rejectedDate) }
        : null,
    };
  }

  // Draft save of the report: persists progress without finalising (submitted: false).
  saveSession(sessionReport: LeapV2SessionReport): Observable<LeapV2SessionSaveResponse> {
    return this.postReport(sessionReport, false, 'saveSession');
  }

  unlockAttendance(placementId: number, sessionId: number): Observable<any> {
    return this.http.post(this.apiUrl + 'api/Session/SessionReport/UnlockAttendance', { placementId: placementId, sessionId: sessionId }, this.prepareOptions({ placementId: placementId, sessionId: sessionId })).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('unlockAttendance'))
    );
  }

  /**
   * Amend — reopens a submitted or approved session so its content can be corrected and resubmitted.
   * Distinct from unlockAttendance: the register stays submitted, so the report is still there to edit
   * and the resubmit needs no re-registering. Distinct from rejectSession: no reason, no email.
   */
  reopenSession(placementId: number, sessionId: number): Observable<any> {
    return this.http.post(this.apiUrl + 'api/Session/SessionReport/Reopen', { placementId: placementId, sessionId: sessionId }, this.prepareOptions({ placementId: placementId, sessionId: sessionId })).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('reopenSession'))
    );
  }

  /**
   * Reject — sends a submitted report back to the tutor with a mandatory reason. The API writes Rejected
   * (with the reason in the submission history) and emails the tutor. Distinct
   * from unlockAttendance, which is the ItAdmin reset and records nothing.
   */
  rejectSession(placementId: number, sessionId: number, reason: string): Observable<any> {
    // Hash exactly the object that is posted (SessionAuthorisation.md §8.6).
    const payload = { placementId: placementId, sessionId: sessionId, reason: reason };
    return this.http.post(this.apiUrl + 'api/Session/SessionReport/Reject', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('rejectSession'))
    );
  }

  /**
   * Accept — authorises a submitted session for payment. The API writes the LeapDb approval and marks
   * the session payable in Orbit; a failure in either leaves the session Submitted.
   */
  authoriseSession(placementId: number, sessionId: number): Observable<any> {
    const payload = { placementId: placementId, sessionId: sessionId };
    return this.http.post(this.apiUrl + 'api/Session/SessionReport/Authorise', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('authoriseSession'))
    );
  }

  /**
   * Records a tutor cancellation against the ORBIT session, not the LeapDb anchor id (bug 37020).
   * The button that reaches this is disabled once any attendance is saved, so the session has no
   * tblSessions rows and no anchor id at the point it is used — orbitSessionId is the only id that
   * exists, and it is the grain the rest of the session report is keyed on.
   */
  tutorCancellation(bookingId: number, orbitSessionId: number, reason: string, comments: string): Observable<any> {
    let payload = {
      bookingId: bookingId,
      orbitSessionId: orbitSessionId,
      reason: reason,
      comments: comments
    }
    return this.http.post(this.apiUrl + 'api/Session/SessionReport/TutorCancellation', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('tutorCancellation'))
    );
  }

  submitBehaviouralConcern(studentId: number, bookingId: number, sessionId: number, reason: string, comments: string): Observable<any>  {
    let payload = {
      studentId: studentId,
      bookingId: bookingId,
      sessionId: sessionId,
      reason: reason,
      comments: comments
    }
    return this.http.post(this.apiUrl + 'api/Session/SessionReport/BehaviouralConcern', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('submitBehaviouralConcern'))
    );
  }

  /**
   * Saves the consultant's notes on a submitted register (staff only). Keyed on the Orbit session like the two form
   * saves; writes only the notes, never the tutor's attendance. A blank string clears them.
   */
  saveConsultantNotes(placementId: number, orbitSessionId: number, notes: string): Observable<boolean> {
    const payload = { placementId: placementId, orbitSessionId: orbitSessionId, notes: notes };
    return this.http.post(this.apiUrl + 'api/Session/SessionReport/ConsultantNotes', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => data.body as boolean),
      catchError(this.handleError('saveConsultantNotes'))
    );
  }

  /**
   * Formats a Date as a local 'yyyy-MM-dd' string for the wire. The API only keeps the date component, so a
   * date-only string is exact — and it avoids Date -> JSON toISOString() (UTC), which shifts the calendar day
   * for non-UTC users and would land a StartDate a day off the one stored (duplicating the student on reload).
   */
  private toDateOnlyString(date: Date | null | undefined): string | null {
    if (!date) { return null; }
    const local = new Date(date);
    const year = local.getFullYear();
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const day = String(local.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Submits the register (Form 1). This saves and locks the register only — the session is settled when
   * the report is submitted (submitProgress), so the response always carries status Open.
   */
  submitAttendance(sessionReport: LeapV2SessionReport): Observable<LeapV2SessionSaveResponse> {
    const session = sessionReport.session;
    let attendance = {
      placementId: sessionReport.placementId,
      orbitSessionId: sessionReport.orbitSessionId,
      date: this.toDateOnlyString(session.date),
      startTime: session.startTime,
      endTime: session.endTime,
      subject: session.subject,
      sessionType: session.sessionType,
      deliveryMode: session.deliveryMode,
      status: session.status,
      attendance: session.attendance,
      attendanceSubmitted: true
    } as LeapV2SessionAttendanceSubmit;

    return this.http.post(this.apiUrl + 'api/Session/SessionReport/Session', attendance, this.prepareOptions(attendance)).pipe(
      map((data: any) => data.body as LeapV2SessionSaveResponse),
      catchError(this.handleError('submitAttendance'))
    );
  }

  /**
   * Finalises the report (submitted: true). The server applies the attendance rule to the saved register and
   * settles the session in Orbit — authorised (status Submitted) or, when every learner cancelled with 1+
   * days notice, cancelled (status Cancelled) — and returns the outcome with the new status.
   */
  submitProgress(sessionReport: LeapV2SessionReport): Observable<LeapV2SessionSaveResponse> {
    return this.postReport(sessionReport, true, 'submitProgress');
  }

  /**
   * Posts the report (Form 2) to api/Session/SessionReport/Report. `submitted` distinguishes a
   * draft save (false) from a finalising submit (true) — the server uses it to drive the status lifecycle.
   */
  private postReport(sessionReport: LeapV2SessionReport, submitted: boolean, errorContext: string): Observable<LeapV2SessionSaveResponse> {
    const students = sessionReport.report.students ?? [];
    // Files picked on this screen that the API has not stored yet; they are sent with their content.
    const newFiles = students
      .flatMap(s => s.progress?.evidence ?? [])
      .map(f => f.file)
      .filter(f => !!f && !this.uploadedFiles.has(f));

    return from(Promise.all(students.map(s => this.toReportStudent(s)))).pipe(
      switchMap(reportStudents => {
        let report = {
          placementId: sessionReport.placementId,
          orbitSessionId: sessionReport.orbitSessionId,
          date: this.toDateOnlyString(sessionReport.report.date),
          students: reportStudents,
          objectives: sessionReport.report.objectives,
          submitted: submitted,
          deliverySetting: sessionReport.report.deliverySetting ?? null,
          // Each learner's list is every file the panel shows, so the API may remove a stored file missing from it.
          evidenceIsComplete: true
        } as LeapV2SessionReportSubmit;
        return this.http.post(this.apiUrl + 'api/Session/SessionReport/Report', report, this.prepareOptions(report));
      }),
      map((data: any) => data.body as LeapV2SessionSaveResponse),
      // Stored now, so a later save or submit sends them by name only. A failed post leaves them to be sent again.
      tap(() => newFiles.forEach(f => this.uploadedFiles.add(f))),
      catchError(this.handleError(errorContext))
    );
  }

  /**
   * A student as the Report endpoint takes it. The evidence picker holds each picked file as a browser File,
   * which JSON drops, so its content goes as base64 (buildEvidenceDtos); the API stores only files that carry
   * content (bug 40317). The picker keeps every picked File and re-emits it on each change, so a file this
   * service has already stored goes by name only, or every save would store another copy.
   */
  private async toReportStudent(student: LeapV2Student): Promise<LeapV2SessionReportStudentSubmit> {
    const evidence = (student.progress?.evidence ?? []).map(f =>
      f.file && this.uploadedFiles.has(f.file) ? { name: f.name, size: f.size, contentType: f.contentType } : f);
    return { ...student, progress: { ...student.progress, evidence: await buildEvidenceDtos(evidence) } };
  }
}
